import React, { useContext, useState } from "react";
import { SubmitHandler, useForm, FormProvider } from "react-hook-form";
import { FormatAnswer, StepFormField } from "../Step.types";
import { AppContext } from "../../../util/context";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import CheckboxButton from "../InputFields/CheckboxButton";
import CheckboxInput from "../InputFields/CheckboxInput";
import DateOptions from "../InputFields/DateOptions";
import EmailInput from "../InputFields/EmailInput";
import FileInput from "../InputFields/FileInput";
import { FormStep } from "./StepForm.types";
import InsuranceDropdown from "../InputFields/SelectDropdown/Insurance";
import DoctorsDropdown from "../InputFields/SelectDropdown/doctors";
import PhoneInput from "../InputFields/PhoneInput";
import SelectDropdown from "../InputFields/SelectDropdown";
import SkipButton from "../Buttons/SkipButton";
import SubmitButton from "../Buttons/SubmitButton";
import TextArea from "../InputFields/TextArea";
import TextInput from "../InputFields/TextInput";
import block from "bem-css-modules";
import styles from "./StepForm.module.scss";
import { checkIfConditionsMet } from "../../Handlers/handlers";
import DatePickerInput from "../InputFields/DatePickerInput";
import DiGACodeInput from "../InputFields/DiGACodeInput";
import { micteraReachoutResult } from "../../../util/functions";
import { Doctor } from "../InputFields/SelectDropdown/SelectDropdown.types";

const StepForm = (props: { stepData: FormStep }) => {
  const context = useContext(AppContext);
  const methods = useForm({
    shouldUnregister: true, // Automatically remove unmounted or empty fields
  });
  const {
    handleStepSubmit,
    settings: { submitButtonText, crm },
  } = context;
  const product = crm?.[0]?.product;
  const {
    stepData: {
      stepSettings: [
        { uuid, label, customNextButton, classes, nextStep, lastStep },
      ],
      formField,
      skipButton,
      activateRecaptcha,
    },
  } = props;

  const b = block(styles);
  const [submitStarted, setSubmitStarted] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = methods;

  // Use the reCAPTCHA hook
  const { executeRecaptcha } = useGoogleReCaptcha();

  const onSubmit: SubmitHandler<any> = async (data) => {
    setSubmitStarted(true);

    // Only execute reCAPTCHA if enabled
    if (activateRecaptcha) {
      if (!executeRecaptcha) {
        alert("reCAPTCHA konnte nicht geladen werden.");
        setSubmitStarted(false);
        return;
      }
      try {
        const token = await executeRecaptcha("submit");
        if (!token) {
          alert("reCAPTCHA-Validierung fehlgeschlagen.");
          setSubmitStarted(false);
          return;
        }
        // Optionally, attach the token to your data
        data.recaptchaToken = token;
      } catch (error) {
        console.error("reCAPTCHA Fehler:", error);
        alert("Ein Fehler ist bei der Sicherheitsüberprüfung aufgetreten.");
        setSubmitStarted(false);
        return;
      }
    }

    // Remove keys with empty values
    const filteredData = Object.fromEntries(
      Object.entries(data).filter(
        ([_, value]) => value !== "" && value !== null && value !== undefined,
      ),
    );

    const formattedAnswers = formField
      .flatMap((el: StepFormField) => {
        let value =
          filteredData[el.label] ?? filteredData[el.settings[0].crmProperty];
        const result: FormatAnswer[] = [];

        if (
          el._modelApiKey === "field_checkbox_button" ||
          el._modelApiKey === "field_consent"
        ) {
          value = el.customCrmValue || value;
        }

        if (el._modelApiKey === "field_consent" && el.timestamp) {
          value = new Date().toISOString();
        }

        if (el._modelApiKey === "field_doctor_selector") {
          if (value) {
            result.push({
              field_label: el.label,
              field_value: (value as { crm_record_id: string }).crm_record_id,
              field_crm_id: el.settings[0].crmProperty,
              push_to_common_crm_field: el.pushToCommonCrmField,
            });
          }

          if (el.manualDoctorInput?.doctorFields) {
            el.manualDoctorInput.doctorFields.forEach(
              (field: StepFormField) => {
                const fieldValue = filteredData[field.label];
                if (fieldValue) {
                  result.push({
                    field_label: field.label,
                    field_value: fieldValue,
                    field_crm_id: field.settings[0].crmProperty,
                    push_to_common_crm_field: false,
                  });
                }
              },
            );
          }

          return result;
        }

        if (value !== undefined) {
          return [
            {
              field_label: el.label,
              field_value: value,
              field_crm_id: el.settings[0].crmProperty,
              push_to_common_crm_field: el.pushToCommonCrmField,
            },
          ];
        }

        return [];
      })
      .reduce((acc, current_item) => {
        if (current_item.push_to_common_crm_field) {
          const existingIndex = acc.findIndex(
            (item) => item.field_crm_id === current_item.field_crm_id,
          );
          if (existingIndex !== -1) {
            acc[existingIndex].field_value +=
              ` ~|~ ${current_item.field_value}`;
          } else {
            acc.push({ ...current_item });
          }
        } else {
          acc.push({ ...current_item });
        }
        return acc;
      }, [] as FormatAnswer[]);

    const stepData = {
      fields: formattedAnswers,
      uuid: uuid,
      label: label,
      settings: {
        tracking: {
          gtmEvent: `formClient.step.${label}`,
        },
        classes,
        custom_next_button: customNextButton,
        next_step: nextStep,
        last_step: lastStep,
      },
    };

    // DTX-300 (Mictera doctor reach-out): surface the chosen doctor's identity
    // and the channels we actually have (email/phone/doctolib) into the session
    // BEFORE submitting, so the next step's channel-availability conditions and
    // the contact steps can read them via ${{...}}. Scoped to Mictera so no
    // other form is affected.
    if (product?.toLowerCase() === "mictera") {
      const doctorField = formField.find(
        (f: StepFormField) => f._modelApiKey === "field_doctor_selector",
      );
      if (doctorField) {
        const selected =
          filteredData[doctorField.label] ??
          filteredData[doctorField.settings[0].crmProperty];
        if (selected && typeof selected === "object") {
          const d = selected as Doctor;
          const channels = [
            d.email ? "email" : "",
            d.phone_number ? "phone" : "",
            d.doctolib_link ? "doctolib" : "",
          ]
            .filter(Boolean)
            .join(",");
          window.sessionLayer.pushToSession("doctor_name", d.full_name || "");
          window.sessionLayer.pushToSession("doctor_email", d.email || "");
          window.sessionLayer.pushToSession("doctor_phone", d.phone_number || "");
          window.sessionLayer.pushToSession("doctor_doctolib", d.doctolib_link || "");
          window.sessionLayer.pushToSession("doctor_channels", channels);
          // Flag whether the chosen doctor has ANY contact channel. The funnel
          // routes doctors with no contact info to the generic "not found" info
          // page instead of an empty channel picker (docNf is gated on this).
          window.sessionLayer.pushToSession("doctor_has_channels", channels ? "yes" : "no");
        }
      }
    }

    handleStepSubmit(context, stepData);

    // Recompute the reach-out helpers AFTER submit so they pick up values this
    // step just wrote to the session (handleDuplicateStep runs synchronously) —
    // e.g. the patient name on the name step feeds the prefilled e-mail body.
    if (product?.toLowerCase() === "mictera") {
      const rx = micteraReachoutResult();
      window.sessionLayer.pushToSession("icd_code", rx.icdCode);
      window.sessionLayer.pushToSession("rx_mail_subject", rx.mailSubject);
      window.sessionLayer.pushToSession("rx_mail_body", rx.mailBody);
      window.sessionLayer.pushToSession("rx_mail_href", rx.mailHref);
      window.sessionLayer.pushToSession("rx_phone_symptom", rx.phoneSymptom);
      window.sessionLayer.pushToSession("rx_tel_href", rx.telHref);
    }

    setSubmitStarted(false);
  };

  return (
    <div className={b()}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {formField?.map((field: StepFormField, i: number) => {
            const { _modelApiKey } = field;
            const { hideIfFieldExists } = field.settings[0];

            if (
              hideIfFieldExists?.length > 0 &&
              checkIfConditionsMet(hideIfFieldExists)
            )
              return null;

            const inputField = () => {
              switch (_modelApiKey) {
                case "field_text":
                  if (field.settings[0].crmProperty === "diga_code") {
                    return (
                      <DiGACodeInput
                        key={i}
                        fieldData={field}
                        register={register}
                        errors={errors}
                        setError={setError}
                        clearErrors={clearErrors}
                      />
                    );
                  }
                  return (
                    <TextInput
                      key={i}
                      fieldData={field}
                      register={register}
                      errors={errors}
                    />
                  );
                case "field_textarea":
                  return (
                    <TextArea
                      key={i}
                      fieldData={field}
                      register={register}
                      errors={errors}
                    />
                  );
                case "field_email":
                  return (
                    <EmailInput
                      key={i}
                      fieldData={field}
                      register={register}
                      errors={errors}
                    />
                  );
                case "field_phone":
                  return (
                    <PhoneInput
                      key={i}
                      fieldData={field}
                      register={register}
                      errors={errors}
                    />
                  );
                case "field_file":
                  return (
                    <FileInput
                      key={i}
                      fieldData={field}
                      errors={errors}
                      clearErrors={clearErrors}
                      setValue={setValue}
                      control={control}
                    />
                  );
                case "field_consent":
                  return (
                    <CheckboxInput
                      key={i}
                      fieldData={field}
                      register={register}
                      errors={errors}
                    />
                  );
                case "field_insurance_selector":
                  return (
                    <InsuranceDropdown
                      key={i}
                      fieldData={field}
                      errors={errors}
                      control={control}
                    />
                  );
                case "field_doctor_selector":
                  return (
                    <DoctorsDropdown
                      key={i}
                      fieldData={field}
                      register={register}
                      errors={errors}
                      control={control}
                    />
                  );
                case "field_dropdown":
                  return (
                    <SelectDropdown
                      key={i}
                      fieldData={field}
                      errors={errors}
                      control={control}
                    />
                  );
                case "field_checkbox_button":
                  return (
                    <CheckboxButton
                      key={i}
                      fieldData={field}
                      register={register}
                      errors={errors}
                    />
                  );
                case "field_dynamic_date":
                  return (
                    <DateOptions
                      key={i}
                      fieldData={field}
                      errors={errors}
                      control={control}
                    />
                  );
                case "field_date_picker":
                  return (
                    <DatePickerInput
                      key={i}
                      fieldData={field}
                      setValue={setValue}
                      clearErrors={clearErrors}
                      errors={errors}
                      control={control}
                    />
                  );
                default:
                  return null;
              }
            };

            return inputField();
          })}
          <div className={`fc-form-control ${b("control")}`}>
            <div className={b("")}>
              <SubmitButton
                submitStarted={submitStarted}
                stepLabel={label}
                submitBtnText={customNextButton || submitButtonText}
              />
            </div>
            {skipButton && (
              <SkipButton
                skipBtnText={skipButton}
                stepLabel={label}
                nextStep={nextStep}
              />
            )}
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default StepForm;
